/**
 * CredentialPickerExistingBlock — FSM gate: awaiting_credential (existing creds)
 *
 * Mockup: designs/ai-dashboard-platform-v3/2026-04-23-05-credential-picker-existing-creds-v3.html
 * Brand guide:
 *   §12 (Component 4 — Credential Pill Picker)
 *   §23 (expiry badge)
 *   §37e v2 (auth_failed blocked row — paste-and-validate modal)
 *   §37g (access_denied blocked row — instructional panel)
 * TDD: §3d.1.E — rendered when availableCredentials.length >= 1
 *     §5.2 — reason-aware click guard (Phase E Slice 2.4)
 *
 * Props:
 * - credentials: list of existing credentials from Redux (availableCredentials selector)
 * - sourceName: display name for the source (e.g. "CampMinder")
 * - sourceAppCode: app code (e.g. 'campminder') for post-success prefetch
 * - onAddNew: callback when user clicks "Add a new connection"
 * - onOpenInstructionalPanel: callback to open the access_denied instructional
 *   side panel at the shell level (push panel — not rendered inside this block)
 *
 * Click-guard rule (TDD §5.2):
 *   Fail-open: absent availabilitySummary OR overall !== 'unavailable' → ADVANCE
 *   overall === 'unavailable' AND blockedReason.reason === 'auth_failed' → BLOCK + modal
 *   overall === 'unavailable' AND blockedReason.reason === 'access_denied' → BLOCK + panel
 *   Blocked credentials render OUTSIDE the radiogroup as role="status" divs.
 *
 * `CredentialOption` is the canonical shared type at `@/types/DashboardV1.types` —
 * import directly from there. Do not re-export from this component file.
 */

import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Lock, ShieldOff, Key, ShieldCheck, Loader2, RefreshCw } from 'lucide-react'
import type { AppDispatch, RootState } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { flowTransitionThunk } from '@/services/DashboardFlowTransitionService'
import { buildCredentialPickTemplates } from '@/services/transitionTemplates'
import { recheckCredentialAvailability } from '@/services/CredentialCapabilityService'
import type { CredentialOption } from '@/types/DashboardV1.types'
import { Button } from '@/components/ui/button'
import { ReenterApiKeyModal } from './ReenterApiKeyModal'
import { AppName } from '@/constants/application/application'
import { AppLogo } from './SourcePickerBlock'

// ─── Availability helpers ─────────────────────────────────────────────────────

type BlockedReason = 'auth_failed' | 'access_denied'

/**
 * isCredentialBlocked — returns the blockedReason if the credential should be
 * rendered as a non-selectable blocked row. Returns null (fail-open) when:
 *   - availabilitySummary is absent
 *   - overall !== 'unavailable'
 *   - blockedReason is absent
 *
 * TDD §5.2 — BE pre-computed, FE reads directly, never re-derives.
 */
function isCredentialBlocked(cred: CredentialOption): BlockedReason | null {
  const summary = cred.availabilitySummary
  if (!summary) return null
  if (summary.overall !== 'unavailable') return null
  if (!summary.blockedReason) return null
  return summary.blockedReason.reason
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface CredentialPickerExistingBlockProps {
  credentials: CredentialOption[]
  sourceName?: string
  /** App code (e.g. 'campminder') for post-success credential re-fetch. */
  sourceAppCode?: string
  onAddNew?: () => void
  /**
   * Called when the user clicks "Re-create API key with scopes" (access_denied).
   * The shell renders the instructional panel as a push sibling — not inside
   * this component. The shell passes the credentialId down so the panel can
   * call PUT /v1/credentials/:credentialId.
   */
  onOpenInstructionalPanel?: (credentialId: string) => void
  /**
   * Whether the instructional panel is currently open for this block's session.
   * Passed from the shell so the access_denied CTA can set aria-expanded.
   */
  instructionalPanelOpenForCredentialId?: string | null
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CredentialPickerExistingBlock = ({
  credentials,
  sourceName = 'CampMinder',
  sourceAppCode = 'campminder',
  onAddNew,
  onOpenInstructionalPanel,
  instructionalPanelOpenForCredentialId = null,
}: CredentialPickerExistingBlockProps) => {
  const dispatch: AppDispatch = useDispatch()
  const token = useSelector((s: RootState) => s.user.token)
  const dashboardId = useSelector((s: RootState) => s.dashboardCreation.dashboardId)
  const { applications } = useSelector((s: RootState) => s.application)

  // Separate blocked credentials (rendered outside radiogroup) from selectable ones.
  const blockedCredentials = credentials.filter(c => isCredentialBlocked(c) !== null)
  const selectableCredentials = credentials.filter(c => isCredentialBlocked(c) === null)

  const [selectedId, setSelectedId] = useState<string>(selectableCredentials[0]?.id ?? '')
  const selectedCredential = selectableCredentials.find(c => c.id === selectedId)

  // Per-credential re-check loading state. Key: credentialId.
  const [recheckingIds, setRecheckingIds] = useState<Set<string>>(new Set())
  // Per-credential "still unavailable" post-recheck message. Key: credentialId.
  const [recheckStillUnavailable, setRecheckStillUnavailable] = useState<Set<string>>(new Set())

  // auth_failed modal state.
  const [reenterModalOpen, setReenterModalOpen] = useState(false)
  const [reenterModalCredentialId, setReenterModalCredentialId] = useState<string>('')
  const [reenterModalCredentialLabel, setReenterModalCredentialLabel] = useState<string>('')

  // Success announcement (role="alert") — cleared after 5s by the browser.
  const [successAnnouncement, setSuccessAnnouncement] = useState<string>('')

  const handleConfirm = () => {
    if (!selectedId || !selectedCredential) return
    dispatch(dashboardCreationActions.setCredentialId(selectedId))
    dispatch(dashboardCreationActions.setFlowState('awaiting_domain'))
    if (token && dashboardId) {
      const templates = buildCredentialPickTemplates(selectedCredential.label, sourceName)
      const idempotencyKey =
        globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
      void dispatch(
        flowTransitionThunk(
          token,
          dashboardId,
          {
            toState: 'awaiting_domain',
            contextDelta: { credentialId: selectedId },
            ...templates,
          },
          idempotencyKey
        )
      )
    }
  }

  const handleAddNew = () => {
    if (onAddNew) {
      onAddNew()
    } else {
      dispatch(dashboardCreationActions.clearHarvest())
      dispatch(dashboardCreationActions.setHarvestContentState('launching'))
    }
  }

  const handleOpenReenterModal = (credId: string, credLabel: string) => {
    setReenterModalCredentialId(credId)
    setReenterModalCredentialLabel(credLabel)
    setReenterModalOpen(true)
  }

  const handleRecheck = async (credId: string) => {
    if (!token) return
    setRecheckingIds(prev => new Set(prev).add(credId))
    setRecheckStillUnavailable(prev => {
      const next = new Set(prev)
      next.delete(credId)
      return next
    })

    const result = await dispatch(recheckCredentialAvailability(token, credId))

    setRecheckingIds(prev => {
      const next = new Set(prev)
      next.delete(credId)
      return next
    })

    if (!result.err && result.data) {
      // setAvailabilityForCredential already dispatched in service thunk.
      // If blockedReason is now absent, the credential will re-render as selectable.
      const isNowAvailable = result.data.availabilitySummary.overall === 'available'
        || !result.data.availabilitySummary.blockedReason
      if (!isNowAvailable) {
        setRecheckStillUnavailable(prev => new Set(prev).add(credId))
      }
    } else {
      // Probe failed entirely — treat as still unavailable.
      setRecheckStillUnavailable(prev => new Set(prev).add(credId))
    }
  }

  const handleSuccess = (credLabel: string) => {
    setSuccessAnnouncement(`${credLabel} is now valid — you can select it.`)
  }

  return (
    <div data-testid="credential-picker-existing-block" className="flex flex-col gap-3 w-full">
      {/* Success announcement — screen-reader-only role="alert" */}
      {successAnnouncement && (
        <p
          className="sr-only"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {successAnnouncement}
        </p>
      )}

      <p className="mb-2">
        I found existing {sourceName} connections for your organization. Which one should I use?
      </p>

      <div
        role="region"
        aria-label={`Select a ${sourceName} credential`}
        className="max-w-[520px]"
      >
        {/* Blocked credentials — rendered OUTSIDE the radiogroup (§37e/§37g) */}
        {blockedCredentials.map(cred => {
          const blockedReason = isCredentialBlocked(cred)
          const summary = cred.availabilitySummary!
          const userMessage = summary.blockedReason?.userMessage ?? ''
          const isRechecking = recheckingIds.has(cred.id)
          const stillUnavailable = recheckStillUnavailable.has(cred.id)
          const isPanelOpenForThis = instructionalPanelOpenForCredentialId === cred.id

          if (blockedReason === 'auth_failed') {
            return (
              <div
                key={cred.id}
                className="w-full border-[1.5px] border-warning/35 bg-warning-foreground rounded-[12px] overflow-hidden mb-1.5"
                role="status"
                aria-label={`${cred.label} — invalid credential, cannot proceed`}
              >
                {/* Pill header */}
                <div className="flex items-center justify-between px-3.5 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-amber-100 border border-warning/35 flex items-center justify-center flex-shrink-0 text-warning">
                      <Lock size={13} aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-900">{cred.label}</p>
                      <p className="text-xs text-amber-700/65 mt-px">Cannot connect — credential is invalid</p>
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-warning bg-amber-100 border border-warning/35 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0">
                    Invalid credential
                  </span>
                </div>

                {/* Affordance strip */}
                <div className="border-t border-warning/35 px-3.5 py-2.5 bg-warning-foreground/70">
                  {userMessage && (
                    <p className="text-[13px] text-amber-800 leading-[1.55] mb-2.5">
                      {userMessage}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* "Re-enter API key" — primary CTA, opens paste-and-validate modal */}
                    <button
                      type="button"
                      className="flex items-center gap-1.5 bg-amber-900 text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:opacity-[0.88] transition-opacity focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
                      aria-label={`Re-enter API key for ${cred.label} — opens validation dialog`}
                      onClick={() => handleOpenReenterModal(cred.id, cred.label)}
                    >
                      <Key size={12} aria-hidden="true" />
                      Re-enter API key
                    </button>
                    {/* "Re-check now" — secondary */}
                    <button
                      type="button"
                      className="flex items-center gap-1.5 bg-transparent border border-warning/35 text-amber-800 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-amber-100/50 transition-colors focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
                      onClick={() => void handleRecheck(cred.id)}
                      disabled={isRechecking}
                      aria-label={`Re-check ${cred.label} validity now`}
                      aria-busy={isRechecking}
                    >
                      {isRechecking
                        ? <><Loader2 size={12} className="animate-spin" aria-hidden="true" />Checking&hellip;</>
                        : <><RefreshCw size={12} aria-hidden="true" />Re-check now</>
                      }
                    </button>
                  </div>
                  {/* Post-recheck still unavailable message */}
                  {stillUnavailable && (
                    <p className="text-[12px] text-amber-800 mt-2">
                      Still unavailable. Try fixing the credential and checking again.
                    </p>
                  )}
                </div>
              </div>
            )
          }

          if (blockedReason === 'access_denied') {
            return (
              <div
                key={cred.id}
                className="w-full border-[1.5px] border-warning/35 bg-warning-foreground rounded-[12px] overflow-hidden mb-1.5"
                role="status"
                aria-label={`${cred.label} — missing required API scopes, cannot proceed`}
              >
                {/* Pill header */}
                <div className="flex items-center justify-between px-3.5 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-amber-100 border border-warning/35 flex items-center justify-center flex-shrink-0 text-warning">
                      <ShieldOff size={13} aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-900">{cred.label}</p>
                      <p className="text-xs text-amber-700/65 mt-px">Key created without required API scopes</p>
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-warning bg-amber-100 border border-warning/35 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0">
                    Missing scopes
                  </span>
                </div>

                {/* Affordance strip */}
                <div className="border-t border-warning/35 px-3.5 py-2.5 bg-warning-foreground/70">
                  {userMessage && (
                    <p className="text-[13px] text-amber-800 leading-[1.55] mb-2.5">
                      {userMessage}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* "Re-create API key with scopes" — primary CTA, opens instructional panel */}
                    <button
                      type="button"
                      className="flex items-center gap-1.5 bg-amber-900 text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:opacity-[0.88] transition-opacity focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
                      aria-label={`Re-create CampMinder API key with all scopes for ${cred.label} — opens step-by-step guide`}
                      aria-haspopup="true"
                      aria-expanded={isPanelOpenForThis}
                      onClick={() => onOpenInstructionalPanel?.(cred.id)}
                    >
                      <ShieldCheck size={12} aria-hidden="true" />
                      Re-create API key with scopes
                    </button>
                    {/* "Re-check now" — secondary */}
                    <button
                      type="button"
                      className="flex items-center gap-1.5 bg-transparent border border-warning/35 text-amber-800 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-amber-100/50 transition-colors focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
                      onClick={() => void handleRecheck(cred.id)}
                      disabled={isRechecking}
                      aria-label={`Re-check ${cred.label} permissions now`}
                      aria-busy={isRechecking}
                    >
                      {isRechecking
                        ? <><Loader2 size={12} className="animate-spin" aria-hidden="true" />Checking&hellip;</>
                        : <><RefreshCw size={12} aria-hidden="true" />Re-check now</>
                      }
                    </button>
                  </div>
                  {stillUnavailable && (
                    <p className="text-[12px] text-amber-800 mt-2">
                      Still unavailable. Try fixing the credential and checking again.
                    </p>
                  )}
                </div>
              </div>
            )
          }

          /* istanbul ignore next — blockedReason can only be auth_failed | access_denied per TypeScript type; this null is a structural exhaustion guard */
          return null
        })}

        {/* Selectable radiogroup — only non-blocked credentials */}
        {selectableCredentials.length > 0 && (
          <div
            role="radiogroup"
            aria-label={`${sourceName} credentials`}
            className="flex flex-col gap-1.5 mb-2.5"
          >
            {selectableCredentials.map(cred => {
              const isSelected = selectedId === cred.id
              return (
                <button
                  key={cred.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={[
                    cred.label,
                    cred.subtitle,
                    cred.expiresInDays != null ? `expires in ${cred.expiresInDays} days` : undefined,
                  ]
                    .filter(Boolean)
                    .join(' — ')}
                  onClick={() => setSelectedId(cred.id)}
                  className={`flex items-center justify-between w-full text-left px-3.5 py-2.5 rounded-xl border-[1.5px] transition-all duration-150 focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2
                    ${isSelected
                      ? 'border-accent bg-accent-background'
                      : 'border-border bg-card hover:border-accent hover:bg-accent-background'
                    }`}
                >
                  <div className="flex items-center gap-2.5">
                    {/* TODO - This should be dynamic, we are showing Campminder Logo only because dashboard support Campminder only */}
                    <AppLogo logoURL={applications.find(app => app.name === AppName.CAMPMINDER)?.logo} />
                    <div>
                      <p className={`text-xs font-semibold ${isSelected ? 'text-accent' : 'text-primary'}`}>
                        {cred.label}
                      </p>
                      {cred.subtitle && (
                        <p className={`text-xs mt-px ${isSelected ? 'text-accent/70' : 'text-muted-foreground'}`}>
                          {cred.subtitle}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Expiry badge */}
                  {cred.expiresInDays != null && cred.expiresInDays <= 30 && (
                    <span
                      className="text-xs font-medium bg-[#FFFBEB] border border-[#FDE68A] rounded px-1.5 py-0.5 text-[#D97706] flex-shrink-0 ml-2"
                      aria-label={`Expires in ${cred.expiresInDays} days`}
                    >
                      Expires in {cred.expiresInDays} days
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {/* Add new (dashed) */}
        <button
          type="button"
          className="flex items-center gap-2 w-full text-left px-3.5 py-2.5 rounded-xl border-2 border-dashed border-border bg-card hover:border-accent hover:bg-accent-background transition-all duration-150 text-sm font-medium text-secondary hover:text-accent focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          aria-label={`Add a new ${sourceName} connection`}
          onClick={handleAddNew}
        >
          <Plus size={14} aria-hidden="true" />
          Add a new {sourceName} connection
        </button>

        {/* Confirm button */}
        <Button
          type="button"
          className="w-full font-semibold mt-2.5"
          disabled={!selectedCredential}
          aria-label={
            selectedCredential
              ? `Use ${selectedCredential.label}`
              : selectableCredentials.length === 0
                ? 'Select a valid credential to continue'
                : 'Select a credential to continue'
          }
          onClick={handleConfirm}
        >
          {selectedCredential ? `Use ${selectedCredential.label}` : 'Select a credential'}
        </Button>
      </div>

      {/* auth_failed paste-and-validate modal */}
      {reenterModalCredentialId && (
        <ReenterApiKeyModal
          open={reenterModalOpen}
          onOpenChange={setReenterModalOpen}
          credentialId={reenterModalCredentialId}
          credentialLabel={reenterModalCredentialLabel}
          sourceAppCode={sourceAppCode}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  )
}
